import React, { useState, useEffect, useRef } from 'react';

const SearchablePropertyDropdown = ({ value, onChange, options = [], isInvalid, placeholder = "Select Property" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || "");
    const [focusedIndex, setFocusedIndex] = useState(-1);
    
    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    // Sync search input with value when dropdown is closed or value changes from parent
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm(value || "");
        }
    }, [value, isOpen]);

    // Handle clicking outside the dropdown to close it and reset text
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm(value || "");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [value]);

    // Filter options based on all search terms (Option A - Full Search)
    const filteredOptions = options.filter(option => {
        if (!searchTerm) return true;
        const terms = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
        const optionLower = option.toLowerCase();
        return terms.every(term => optionLower.includes(term));
    });

    // Reset focused index whenever filter results change
    useEffect(() => {
        setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
    }, [searchTerm, options.length, filteredOptions.length]);

    // Scroll focused element into view
    useEffect(() => {
        if (focusedIndex >= 0 && listRef.current) {
            const focusedElement = listRef.current.children[focusedIndex];
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                });
            }
        }
    }, [focusedIndex]);

    const handleSelect = (option) => {
        onChange(option);
        setSearchTerm(option);
        setIsOpen(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === "ArrowDown" || e.key === "Enter") {
                setIsOpen(true);
                setFocusedIndex(0);
            }
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (filteredOptions.length > 0) {
                setFocusedIndex(prev => (prev + 1) % filteredOptions.length);
            }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (filteredOptions.length > 0) {
                setFocusedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filteredOptions.length > 0 && focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
                handleSelect(filteredOptions[focusedIndex]);
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setSearchTerm(value || "");
        }
    };

    // Helper to highlight matching terms inside option text
    const highlightMatch = (text, query) => {
        if (!query) return <span>{text}</span>;
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) return <span>{text}</span>;
        
        // Escape special regex characters in terms
        // eslint-disable-next-line no-useless-escape
        const escapedTerms = terms.map(term => term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
        const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
        
        const parts = text.split(regex);
        return (
            <span>
                {parts.map((part, i) => 
                    regex.test(part) ? (
                        <strong key={i} className="highlighted-term">
                            {part}
                        </strong>
                    ) : (
                        <span key={i}>{part}</span>
                    )
                )}
            </span>
        );
    };

    return (
        <div className={`searchable-property-dropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
            <div className="searchable-property-input-wrapper">
                <input
                    type="text"
                    className={`form-control searchable-property-input ${isInvalid ? 'is-invalid' : ''}`}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={(e) => {
                        setIsOpen(true);
                        e.target.select();
                    }}
                    onKeyDown={handleKeyDown}
                />
                <svg 
                    className="searchable-property-chevron" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </div>
            
            {isOpen && (
                <div className="searchable-dropdown-menu" ref={listRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, idx) => (
                            <div
                                key={idx}
                                className={`searchable-dropdown-item ${idx === focusedIndex ? 'focused' : ''}`}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setFocusedIndex(idx)}
                            >
                                {highlightMatch(option, searchTerm)}
                            </div>
                        ))
                    ) : (
                        <div className="searchable-dropdown-no-results">
                            No properties found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchablePropertyDropdown;
